import React, { useMemo, useState } from 'react'

import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client'

import {
  DESTROY_CUSTOMER_SPREEDLY_CARD,
  DESTROY_CUSTOMER_SQUARE_CARD,
  GET_CURRENT_CUSTOMER_PAYMENT_CARDS,
  SET_CUSTOMER_SPREEDLY_CARD_AS_PRIMARY,
  SET_CUSTOMER_SQUARE_CARD_AS_PRIMARY,
} from 'Const'
import { ShopCustomerSpreedlyCard } from 'Types'
import { noty } from 'Utils'

import { QueryState } from '../../components/QueryState'

import { PaymentCard } from './Card'

const addPaymentMethod = (): void => {
  console.log('[stub] addPaymentMethod')
  noty('Adding a card isn’t available here yet.', 'info')
}

interface Props {
  isSpreedlyGateway: boolean
  isSquareGateway: boolean
}

export const PaymentTab: React.FC<Props> = ({ isSpreedlyGateway, isSquareGateway }) => {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data, loading, error } = useQuery(GET_CURRENT_CUSTOMER_PAYMENT_CARDS)
  const customerId = data?.currentCustomer?.id
  const cardTypename = isSpreedlyGateway ? 'ShopCustomerSpreedlyCard' : 'ShopSquareCustomerCard'
  const cardsField = isSpreedlyGateway ? 'spreedlyCards' : 'squareCards'
  const cards = useMemo((): ShopCustomerSpreedlyCard[] => {
    if (isSpreedlyGateway) return data?.currentCustomer?.spreedlyCards ?? []
    if (isSquareGateway) return data?.currentCustomer?.squareCards ?? []
    return []
  }, [data, isSpreedlyGateway, isSquareGateway])

  const evictCard = (cache: ApolloCache<unknown>, deletedId: string | undefined) => {
    if (!deletedId || !customerId) return
    cache.evict({ id: cache.identify({ __typename: cardTypename, id: deletedId }) })
    cache.modify({
      id: cache.identify({ __typename: 'Customer', id: customerId }),
      fields: {
        [cardsField]: (existing: readonly Reference[] = [], { readField }) =>
          existing.filter((ref) => readField('id', ref) !== deletedId),
      },
    })
    cache.gc()
  }

  const markCardPrimary = (cache: ApolloCache<unknown>, primaryId: string | undefined) => {
    if (!primaryId) return
    cards.forEach((card) => {
      cache.modify({
        id: cache.identify({ __typename: cardTypename, id: card.id }),
        fields: { primary: () => card.id === primaryId },
      })
    })
  }

  const [destroySpreedlyCard] = useMutation(DESTROY_CUSTOMER_SPREEDLY_CARD, {
    update: (cache, { data: mutationData }) =>
      evictCard(cache, mutationData?.destroyCustomerSpreedlyCard?.customerSpreedlyCard?.id as string | undefined),
  })
  const [destroySquareCard] = useMutation(DESTROY_CUSTOMER_SQUARE_CARD, {
    update: (cache, { data: mutationData }) =>
      evictCard(cache, mutationData?.destroyCustomerSquareCard?.customerSquareCard?.id as string | undefined),
  })
  const [setSpreedlyCardAsPrimary] = useMutation(SET_CUSTOMER_SPREEDLY_CARD_AS_PRIMARY, {
    update: (cache, { data: mutationData }) =>
      markCardPrimary(
        cache,
        mutationData?.setAsPrimaryCustomerSpreedlyCard?.customerSpreedlyCard?.id as string | undefined,
      ),
  })
  const [setSquareCardAsPrimary] = useMutation(SET_CUSTOMER_SQUARE_CARD_AS_PRIMARY, {
    update: (cache, { data: mutationData }) =>
      markCardPrimary(
        cache,
        mutationData?.setAsPrimaryCustomerSquareCard?.customerSquareCard?.id as string | undefined,
      ),
  })

  const destroyCard = isSpreedlyGateway ? destroySpreedlyCard : destroySquareCard
  const setCardAsPrimary = isSpreedlyGateway ? setSpreedlyCardAsPrimary : setSquareCardAsPrimary

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await destroyCard({ variables: { input: { id } } })
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to delete this payment method', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const handleSetPrimary = async (id: string) => {
    try {
      await setCardAsPrimary({ variables: { input: { id } } })
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to update this payment method', 'error')
    }
  }

  return (
    <div className="user-profile-panel user-profile-panel_enter">
      <section className="user-profile-section">
        <h2 className="user-profile-section__title">Payment Methods</h2>
        <QueryState
          emptyLabel="No payment methods yet"
          error={error}
          isEmpty={!loading && !error && cards.length === 0}
          loading={loading}
        >
          <div className="user-profile-wallet-grid">
            {cards.map((card) => (
              <PaymentCard
                key={card.id}
                card={card}
                deleting={deletingId === card.id}
                onDelete={(id) => void handleDelete(id)}
                onSetPrimary={(id) => void handleSetPrimary(id)}
              />
            ))}
          </div>
        </QueryState>

        <button className="btn btn-accent" type="button" onClick={addPaymentMethod}>
          Add Payment Method
        </button>
      </section>
    </div>
  )
}
